import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import styles from "./Subject.module.css";
import { CustomButton, CustomTextInput } from "../../../../components";
import { FaCalendarAlt } from "react-icons/fa";
import PopupModal from "../../../../components/PopupModal/PopupModal";

const CreateSubject = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const editData = location.state || null;
  const isEditMode = Boolean(editData?.ID);

  const user = TokenService.getUser();
  const created_by = user?.id || 1;
  const updated_by = user?.id || 1;

  const [subjects, setSubjects] = useState([{ subject_name: "" }]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  // Modal Popup
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",   // "success" | "error"
    message: "",
    redirectPath: "",
  });
  /* ===========================
     PREFILL FOR EDIT
     =========================== */
  useEffect(() => {
    if (isEditMode) {
      setSubjects([
        {
          subject_name: editData?.data?.subject_name || "",
        },
      ]);
    }
  }, [isEditMode, editData]);

  /* ===========================
     ADD SUBJECT (CREATE ONLY)
     =========================== */
  const handleAddSubject = () => {
    setSubjects((prev) => [...prev, { subject_name: "" }]);
  };

  /* ===========================
     REMOVE SUBJECT (CREATE ONLY)
     =========================== */
  const handleRemoveSubject = (index) => {
    if (subjects.length === 1) return;

    setSubjects(subjects.filter((_, i) => i !== index));

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`subject_${index}`];
      return newErrors;
    });
  };

  /* ===========================
     CHANGE
     =========================== */
  const handleChange = (index, value) => {
    const updated = [...subjects];
    updated[index].subject_name = value;
    setSubjects(updated);

    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[`subject_${index}`];
      return newErrors;
    });
  };

  /* ===========================
     VALIDATION
     =========================== */
  const validate = () => {
    const newErrors = {};
    const rawValues = subjects.map((s) => s.subject_name.trim());
    const normalizedValues = rawValues.map((v) => v.toLowerCase());

    rawValues.forEach((value, index) => {
      if (!value) {
        newErrors[`subject_${index}`] = "Subject name is required";
      }
    });

    if (!isEditMode) {
      normalizedValues.forEach((value, index) => {
        if (value && normalizedValues.indexOf(value) !== index) {
          newErrors[`subject_${index}`] = "Duplicate subject name";
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ===========================
     SUBMIT
     =========================== */
  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);

    try {
      let response;
      if (isEditMode) {
        // 🔁 UPDATE SUBJECT
        response = await AxiosInstance.put("/school/updateSubject", {
          id: editData.ID,
          subject_name: subjects[0].subject_name.trim(),
          updated_by,
        });

        if (response.data?.status) {
          setModalState({
            open: true,
            type: "success",
            message: response.data?.message || "Subject updated successfully",
            redirectPath: "/user/ListSubject",
          });

        } else {
          setModalState({
            open: true,
            type: "error",
            message: response.data?.message || "Subject update failed",
            redirectPath: "",
          });
        }
      } else {
        // ➕ CREATE SUBJECT
        const subjectNames = subjects
          .map((s) => s.subject_name.trim())
          .filter(Boolean);

        response = await AxiosInstance.post("/school/createSubject", {
          subject_name: subjectNames,
          created_by,
        });

        if (response.data?.status) {
          setModalState({
            open: true,
            type: "success",
            message: response.data?.message || "Subjects created successfully",
            redirectPath: "/user/ListSubject",
          });
        } else {
          setModalState({
            open: true,
            type: "error",
            message: response.data?.message || "Subject creation failed",
            redirectPath: "",
          });
        }

      }
    } catch (err) {
      console.error(err);
      setModalState({
        open: true,
        type: "error",
        message: err?.response?.data?.message || "Operation failed",
        redirectPath: "",
      });
    } finally {
      setLoading(false);
    }
  };
  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({
      ...prev,
      open: false,
    }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };
  return (
    <>
      <PopupModal
        open={modalState.open}
        title={modalState.type === "success" ? "Success" : "Failed"}
        message={modalState.message}
        onOk={handleSuccessOk}
      />
      <div className={styles.card}>
        <div className={styles.container}>
          {/* HEADER */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaCalendarAlt />
            </div>
            <h1 className={styles.headerTitle}>
              {isEditMode ? "Update Subject" : "Create Subject"}
            </h1>
          </div>

          {/* SUBJECT INPUTS */}
          {subjects.map((item, index) => (
            <div key={index} className="mb-3">
              <div className="row">
                <div className="col-md-4 px-2">
                  <CustomTextInput
                    label="Subject Name"
                    value={item.subject_name}
                    error={errors[`subject_${index}`]}
                    placeholder="Enter subject name"
                    onChange={(e) => handleChange(index, e.target.value)}
                    required
                  />
                </div>
              </div>

              {/* ACTIONS – CREATE ONLY */}
              {!isEditMode && (
                <div className="row mt-2">
                  <div className="col d-flex justify-content-end gap-2">
                    {subjects.length > 1 && (
                      <CustomButton
                        variant="contained"
                        fullWidth={false}
                        onClick={() => handleRemoveSubject(index)}
                        style={{
                          backgroundColor: "#dc3545",
                          borderColor: "#dc3545",
                          color: "#fff",
                        }}
                      >
                        Remove
                      </CustomButton>
                    )}

                    {index === subjects.length - 1 && (
                      <CustomButton

                        variant="contained"
                        fullWidth={false}
                        onClick={handleAddSubject}
                        style={{
                          backgroundColor: "#ff7a18",
                          borderColor: "#ff7a18",
                          color: "#fff",
                        }}
                      >
                        Add Subject
                      </CustomButton>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* SUBMIT */}
          <div className="d-flex justify-content-center mt-4">
            <CustomButton
              onClick={handleSubmit}
              type="button"
              variant="contained"
              fullWidth={false}
              loading={loading}
              style={{
                backgroundColor: "#ff7a18",
                borderColor: "#ff7a18",
                color: "#fff",
              }}
            >
              {isEditMode ? "Update Subject" : "Create Subjects"}
            </CustomButton>
          </div>
        </div>
      </div>

    </>

  );
};

export default CreateSubject;
